import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography, Button } from "@mui/material";
import withToast from "../../hocs/withToast";
import moment from "moment/moment";
import "react-toastify/dist/ReactToastify.css";
import {
  searchIcon,
  phoneIcon,
  emailIcon
} from "../../assets/Icons";
import FormCard from "../../components/FormCard";
import { getPatientDetails, getPatientOptionsList, resetState, editPatient } from "../../redux/reducers/patientReducer";
import FormFilterCard from "../../components/FormFilterCard";
import { getMasterData } from "../../redux/reducers/masterDataReducer";
import Loader from "../../components/Loader";
import { getCareTeamUserList } from "../../redux/reducers/careTeamReducer";


const handlePatientOptions = (patientsOptions = []) => {
  return patientsOptions?.map(ele => ({
    userCredId: ele.userCredId,
    patientsName: `${ele.patientFirstName} ${ele.patientLastName}`,
    patientId: ele.patientId,
    gender: ele.gender,
    email: ele.email,
    birthDay: ele.birthDay ? moment(ele.birthDay).format('LL') : null,
    patientFirstName: ele.patientFirstName,
    patientLastName: ele.patientLastName,
    serialNumber: ele.serialNumber,
    address: ele.address1,
    phoneNo: ele.phoneNo,
    insuranceDetails: ele.insuranceDetails,
    careTeam: ele.careTeam
  }))
}

const PatientEnrollment = ({ notify }) => {
  const dispatch = useDispatch();
  const { userType } = useSelector((state) => state.auth);
  const { patientsOptions = [], loading, currentPatient = {} } = useSelector((state) => state?.patient);
  const { careTeamList = [] } = useSelector((state) => state?.careTeam);
  const { list = {} } = useSelector((state) => state?.masterData);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showErrors, setShowError] = useState(false)

  useEffect(() => {
    dispatch(getPatientOptionsList());
    dispatch(getMasterData('getGender'))
    dispatch(getMasterData('KnownDieasesList'))

    if (userType === 'OrgAdmin')
      dispatch(getCareTeamUserList());
    return () => {
      setSelectedPatient(null)
      dispatch(resetState())
    }
  }, [dispatch, userType]);

  const careTeamOptions = useMemo(() => {
    return careTeamList?.length && careTeamList.map(ele => ({
      id: ele.careUserId,
      value: `${ele.careUserFirstName} ${ele.careUserLastName}`
    }));
  }, [careTeamList]);

  useEffect(() => {
    if (currentPatient?.patientId) {
      setSelectedPatient(currentPatient);
    }
  }, [currentPatient]);

  const handlePatientSelect = (patient) => {
    const { patientId, userCredId } = patient;
    dispatch(getPatientDetails({ patientId, userCredId }))
  };

  const processedPatients = useMemo(() => handlePatientOptions(patientsOptions), [patientsOptions]);

  const filteredPatients = useMemo(() => {
    if (!searchQuery) return processedPatients;
    return processedPatients?.filter((p) =>
      p.patientsName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, processedPatients]);

  const handleSearchChange = (event) => {
    setSearchQuery(event?.target?.value);
  };

  const handlePatientUpdate = (updatedField, value) => {
    setSelectedPatient((prev) => ({ ...prev, [updatedField]: value }));
  };

  const validateForm = (formData) => {
    if (!formData && !formData?.patientId)
      return notify('Please Select Patient to update', 'error')
    if (!formData.patientFirstName)
      return 'First Name is Required'
    if (!formData.patientLastName)
      return 'Last Name is Required'
    if (!formData.birthDay)
      return 'Birthday is Required'
    if (!formData.gender)
      return 'Please Select Gender'
  }

  const handleSubmit = () => {
    const errors = validateForm(selectedPatient)
    if (errors)
      return notify(errors, "error")

    console.log("Updated Patient Data:", selectedPatient);
    dispatch(editPatient(selectedPatient))
      .unwrap()
      .then(res => {
        notify(res.message, "success");
        dispatch(getPatientOptionsList());
        setSelectedPatient(null);
      })
      .catch((err) => {
        notify(err.message, "error");
      });
  };


  let patientInsuranceDetails = {};
  let patientContactDetails = {};

  const insurance = selectedPatient?.insuranceDetails;
  patientInsuranceDetails = typeof insurance === "string" ? JSON.parse(insurance) : insurance ?? {};

  const emergencyContact = Array.isArray(selectedPatient?.contact) ? selectedPatient.contact[0] : null;
  patientContactDetails = typeof emergencyContact === "string" ? JSON.parse(emergencyContact) : emergencyContact ?? {};


  return (
    <Grid container spacing={2}>
      <Grid item xs={12} display="flex" alignItems="center">
        <Typography variant="h4" fontWeight={700} ml={1}>
          Patient Enrollment
        </Typography>
      </Grid>
      {/* basic Section */}
      <Grid item xs={12} md={6}>
        <FormFilterCard
          title="Basic"
          fields={[
            {
              name: "search",
              placeholder: "Search By Patient Name",
              icon: searchIcon,
              fullWidth: true,
              value: searchQuery,
              onChange: (value) => handleSearchChange(value),
            },
            {
              name: "firstName",
              placeholder: "First Name",
              value: selectedPatient?.patientFirstName || "",
              onChange: (value) => handlePatientUpdate("patientFirstName", value),
            },
            {
              name: "lastName",
              placeholder: "Last Name",
              value: selectedPatient?.patientLastName || "",
              onChange: (value) => handlePatientUpdate("patientLastName", value),
            },
            {
              name: "birthDay",
              value: selectedPatient?.birthDay ? moment(selectedPatient?.birthDay).format('YYYY-MM-DD') : null,
              type: 'datePicker',
              onChange: (value) =>
                handlePatientUpdate("birthDay", value),
            },
            {
              name: "gender",
              placeholder: "Gender",
              type: 'select',
              options: list.getGender || [],
              value: selectedPatient?.gender ?? "",
              onChange: (value) =>
                handlePatientUpdate("gender", value),
            },
          ]}
          onPatientSelect={handlePatientSelect}
          filteredPatients={filteredPatients?.slice(0, 5)}
          handleSearchChange={handleSearchChange}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showErrors={showErrors}
        />
      </Grid>

      {/* Contact Section */}
      <Grid item xs={12} md={6}>
        <FormCard
          title="Patient Contact"
          fields={[
            {
              name: "address",
              placeholder: "Address",
              icon: searchIcon,
              fullWidth: true,
              value: selectedPatient?.address || "",
              onChange: (value) =>
                handlePatientUpdate("address", value)
            },
            {
              name: "phone",
              placeholder: "Phone Number",
              icon: phoneIcon,
              fullWidth: true,
              value: selectedPatient?.phoneNo || "",
              onChange: (value) =>
                handlePatientUpdate("phoneNo", value)
            },
            {
              name: "email",
              placeholder: "Email",
              value: selectedPatient?.email ?? "",
              fullWidth: true,
              isDisabled: true,
              onChange: (value) =>
                handlePatientUpdate("email", value),
            },
          ]}
        />
      </Grid>

      {/* Emergency Contact Section */}
      <Grid item xs={12} md={6}>
        <FormCard
          title="Emergency Contact"
          fields={[
            {
              name: "contactName",
              placeholder: "Contact Name",
              fullWidth: true,
              value: patientContactDetails.contact_name || "",
              onChange: (value) =>
                handlePatientUpdate("emergencyContact", {
                  ...selectedPatient.emergencyContact,
                  name: value,
                }),
            },
            {
              name: "email",
              placeholder: "Email",
              icon: phoneIcon,
              value: patientContactDetails.contact_email || "",
              isDisabled: true,
              onChange: (value) =>
                handlePatientUpdate("emergencyContact", {
                  ...selectedPatient.emergencyContact,
                  email: value,
                }),
            },
            {
              name: "phone",
              placeholder: "Phone Number",
              icon: phoneIcon,
              value: patientContactDetails.contact_phone || "",
              onChange: (value) =>
                handlePatientUpdate("emergencyContact", {
                  ...selectedPatient.emergencyContact,
                  phone: value,
                }),
            },
          ]}
        />
      </Grid>

      {/* Insurance Section */}
      <Grid item xs={12} md={6}>
        <FormCard
          title="Insurance"
          fields={[
            {
              name: "company",
              placeholder: "Company Name",
              icon: searchIcon,
              fullWidth: true,
              value: patientInsuranceDetails?.company || "",
              onChange: (value) =>
                handlePatientUpdate("insuranceDetails", {
                  ...selectedPatient.insuranceDetails,
                  company: value,
                }),
            },
            {
              name: "plan",
              placeholder: "Plan",
              value: patientInsuranceDetails?.insurance_plan || "",
              onChange: (value) =>
                handlePatientUpdate("insuranceDetails", {
                  ...selectedPatient.insuranceDetails,
                  insurancePlan: value,
                }),
            },
            {
              name: "policy",
              placeholder: "Policy Number",
              value: patientInsuranceDetails?.policy_number || "",
              onChange: (value) =>
                handlePatientUpdate("insuranceDetails", {
                  ...selectedPatient.insuranceDetails,
                  policyNumber: value,
                }),
            },
          ]}
        />
      </Grid>

      {/* Care Team Section */}
      {userType === 'OrgAdmin' &&
        <Grid item xs={12} md={6}>
          <FormCard
            title="Care Team"
            fields={[
              {
                name: "careTeam",
                // placeholder: "Care Team",
                fullWidth: true,
                multiSelect: true,
                options: careTeamOptions || [],
                value: selectedPatient?.careTeam ?? [],
                onChange: (value) => handlePatientUpdate("careTeam", value),
              }
            ]}
          />
        </Grid>
      }

      {/* Medical History Section */}
      <Grid item xs={12} md={6}>
        <FormCard
          title="Medical History"
          fields={[
            {
              name: "knownDisease",
              fullWidth: true,
              multiSelect: true,
              options: list.KnownDieasesList || [],
              value: Array.isArray(selectedPatient?.knownDisease) ? selectedPatient?.knownDisease : JSON.parse(selectedPatient?.knownDisease || "[]"),
              onChange: (value) =>
                handlePatientUpdate("knownDisease", value),
            },
          ]}
        />
      </Grid>



      {/* Device Section */}
      <Grid item xs={12}>
        <FormCard
          title="Device"
          fields={[
            {
              name: "serialNumber",
              placeholder: "Search by Name",
              icon: searchIcon,
              fullWidth: true,
              isDisabled: true,
              value: selectedPatient?.serialNumber || "",
              onChange: (value) =>
                handlePatientUpdate("serialNumber", value),
            },
          ]}
        />
      </Grid>

      {/* Submit and Cancel Section */}
      <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          sx={{ borderRadius: "20px", backgroundColor: "#000", color: "#fff" }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          sx={{ borderRadius: "20px", color: "#000", borderColor: "#000" }}
          onClick={() => setSelectedPatient(null)}
        >
          Clear
        </Button>
      </Grid>
      <Loader loading={loading} />
    </Grid>
  );
};
export default withToast(PatientEnrollment);
